import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, TextField } from "@mui/material";

interface EditableTableCellProps {
  value: string | number; // The editable value (used when entering edit mode)
  displayValue?: React.ReactNode; // The display value (shown when not editing)
  onSave: (newValue: string) => void;
  onChange?: (newValue: string, cursorPosition: number) => void; // Optional: called on every keystroke
  onEditStart?: () => void; // Called when entering edit mode
  onEditEnd?: () => void; // Called when exiting edit mode
  clipText?: boolean;
  width?: number | string;
  rowHeight?: number;
  // Hook to get active edit state (avoids prop drilling and unnecessary re-renders)
  useActiveEditHook?: () => {
    tempId: string;
    columnField: string;
    cursorPosition: number;
    selectAll: boolean;
  } | null;
  rowId?: string; // Row identifier to match against activeEdit.tempId
  columnField?: string; // Column field name to match against activeEdit.columnField
}

const EditableTableCellComponent: React.FC<EditableTableCellProps> = ({
  value,
  displayValue,
  onSave,
  onChange,
  onEditStart,
  onEditEnd,
  clipText = true,
  width,
  rowHeight = 40,
  useActiveEditHook,
  rowId,
  columnField,
}) => {
  // Get active edit state from hook (if provided)
  const activeEdit = useActiveEditHook?.();

  // Determine if this cell should be in edit mode
  const shouldBeInEditMode =
    activeEdit !== null &&
    activeEdit !== undefined &&
    rowId === activeEdit.tempId &&
    columnField === activeEdit.columnField;

  const initialCursorPosition = shouldBeInEditMode
    ? activeEdit?.cursorPosition
    : undefined;
  const shouldSelectAll = shouldBeInEditMode
    ? (activeEdit?.selectAll ?? false)
    : false;

  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(String(value));
  const [originalValue] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track editing state in a ref to survive re-renders
  const isEditingRef = useRef(false);

  // Wrapper to update both state and ref
  const setIsEditingWithRef = useCallback((editing: boolean) => {
    isEditingRef.current = editing;
    setIsEditing(editing);
  }, []);

  // Update current value when prop changes
  useEffect(() => {
    if (!isEditing) {
      setCurrentValue(String(value));
    }
  }, [value, isEditing]);

  // Maintain focus across re-renders when editing
  useEffect(() => {
    if (
      isEditingRef.current &&
      inputRef.current &&
      document.activeElement !== inputRef.current
    ) {
      // Restore focus if we were editing and lost it due to parent re-render
      inputRef.current.focus();
    }
  });

  // Debounced auto-save: trigger after 5 seconds of no typing
  useEffect(() => {
    if (isEditing && currentValue !== originalValue) {
      // Clear previous timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Set new timeout
      saveTimeoutRef.current = setTimeout(() => {
        handleSave();
      }, 5000);
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [currentValue, isEditing]);

  // Sync edit mode with Redux state
  useEffect(() => {
    if (shouldBeInEditMode && !isEditing) {
      // Enter edit mode when Redux says we should
      setIsEditingWithRef(true);
    } else if (!shouldBeInEditMode && isEditing) {
      // Exit edit mode when Redux says we shouldn't be editing anymore
      setIsEditingWithRef(false);
    }
  }, [shouldBeInEditMode, isEditing, setIsEditingWithRef]);

  // Restore cursor position after entering edit mode or when position changes
  // BUT: Skip this if we should select all (we want to select all instead)
  useEffect(() => {
    if (
      isEditing &&
      inputRef.current &&
      initialCursorPosition !== undefined &&
      !shouldSelectAll // Don't restore cursor if we're selecting all
    ) {
      inputRef.current.setSelectionRange(
        initialCursorPosition,
        initialCursorPosition
      );
    }
  }, [isEditing, initialCursorPosition, shouldSelectAll]);

  const handleSave = () => {
    if (currentValue !== originalValue) {
      onSave(currentValue);
    }
    // Don't set isEditing = false here! Let Redux control it via onEditEnd
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
  };

  const handleCancel = () => {
    setCurrentValue(originalValue);
    // Don't set isEditing = false here! Let Redux control it via onEditEnd
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    // Stop propagation to prevent row click from triggering
    e.stopPropagation();
    e.preventDefault(); // Prevent any default mousedown behavior

    setIsEditingWithRef(true);

    // Delay onEditStart to let the TextField mount and focus first
    setTimeout(() => {
      onEditStart?.(); // Notify parent that edit started (will set selectAll=true in Redux)
    }, 0);
  };

  const handleBlur = () => {
    // Only call onEditEnd if we haven't already called it via Enter/Escape
    // We need to check shouldBeInEditMode from Redux instead of local state
    setTimeout(() => {
      // Only proceed if Redux still says we should be editing
      // (If Enter was pressed, Redux already cleared activeEdit, so shouldBeInEditMode will be false)
      if (shouldBeInEditMode) {
        handleSave();
        onEditEnd?.(); // Notify parent that edit ended
      }
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      handleCancel();
      onEditEnd?.(); // Notify parent that edit ended → Redux sets activeEdit=null → shouldBeInEditMode=false → cell exits edit mode
    } else if (e.key === "Enter") {
      handleSave();
      onEditEnd?.(); // Notify parent that edit ended → Redux sets activeEdit=null → shouldBeInEditMode=false → cell exits edit mode
    }
  };

  // Focus when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();

      // If shouldSelectAll is true (from Redux), select all text
      if (shouldSelectAll) {
        inputRef.current.select();
      }
      // Otherwise, cursor position will be handled by the cursor position effect
    }
  }, [isEditing, shouldSelectAll]);

  if (isEditing) {
    return (
      <TextField
        inputRef={inputRef}
        value={currentValue}
        onChange={(e) => {
          const newValue = e.target.value;
          const cursorPos = e.target.selectionStart || 0;
          setCurrentValue(newValue);
          // Call onChange callback if provided (for live updates)
          if (onChange) {
            onChange(newValue, cursorPos);
          }
        }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        size="small"
        variant="standard"
        fullWidth
        sx={{
          border: "1px solid #1976d2",
          borderRadius: "2px",
          backgroundColor: "#ffffff",
          "& .MuiInputBase-root": {
            fontSize: "14px",
            fontWeight: 500,
            padding: "0",
            lineHeight: "1.5",
            "&:before, &:after": {
              display: "none", // Hide the underline
            },
          },
          "& .MuiInput-input": {
            padding: "0",
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "1.5",
            whiteSpace: clipText ? "nowrap" : "normal",
            wordBreak: clipText ? "normal" : "break-word",
            overflow: clipText ? "hidden" : "visible",
          },
        }}
      />
    );
  }

  return (
    <Box
      onMouseDown={handleClick}
      sx={{
        cursor: "text",
        padding: "0",
        minHeight: "21px",
        display: "flex",
        alignItems: "center",
        fontSize: "14px",
        fontWeight: 500,
        lineHeight: "1.5",
        whiteSpace: clipText ? "nowrap" : "normal",
        wordBreak: clipText ? "normal" : "break-word",
        overflow: clipText ? "hidden" : "visible",
        textOverflow: clipText ? "ellipsis" : "",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.02)",
        },
      }}
    >
      {displayValue !== undefined ? displayValue : currentValue}
    </Box>
  );
};

// Memoize the component to prevent unnecessary re-renders
// The key is that we call useActiveEditHook inside the component to check if WE are active
// So we only re-render when OUR specific cell's active state changes
export const EditableTableCell = React.memo(
  EditableTableCellComponent,
  (prevProps, nextProps) => {
    // The only props that should cause re-renders:
    // 1. value/displayValue changes
    // 2. useActiveEditHook changes (shouldn't happen - it's stable)
    // 3. rowId/columnField changes (cell identity changed)

    // Note: We DON'T check activeEdit here because each cell reads it internally via the hook
    // The hook will only trigger a re-render if THIS specific cell becomes active/inactive

    return (
      prevProps.value === nextProps.value &&
      prevProps.displayValue === nextProps.displayValue &&
      prevProps.useActiveEditHook === nextProps.useActiveEditHook &&
      prevProps.rowId === nextProps.rowId &&
      prevProps.columnField === nextProps.columnField &&
      prevProps.clipText === nextProps.clipText
    );
  }
);
